import React, { ComponentType } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Phone, Calendar, MapPin, Star, ShoppingBag, TrendingUp, Clock, Award, MessageSquare, Heart, QrCode, ShoppingCart, Bike, CalendarClock } from 'lucide-react';
import { Customer, CustomerTag, getCustomerMetrics, tagMeta, useCRM, methodLabels } from './CRMContext';
import { Branch } from './BranchesContext';
import { Toggle } from './Toggle';
import { TagPill } from './CRMPage';
const allTags: CustomerTag[] = ['vip', 'regular', 'new', 'dormant', 'birthday-month'];
const methodIcons: Record<string, ComponentType<{
  size?: number;
  className?: string;
}>> = {
  dineIn: QrCode,
  takeout: ShoppingCart,
  delivery: Bike,
  orderAhead: CalendarClock
};
const methodColors: Record<string, string> = {
  dineIn: 'bg-purple-100 text-purple-700',
  takeout: 'bg-orange-100 text-orange-700',
  delivery: 'bg-blue-100 text-blue-700',
  orderAhead: 'bg-emerald-100 text-emerald-700'
};
export function CustomerProfilePanel({
  customer,
  onClose,
  branches
}: {
  customer: Customer | null;
  onClose: () => void;
  branches: Branch[];
}) {
  const {
    updateCustomer,
    toggleTag
  } = useCRM();
  return <AnimatePresence data-id="element-1814">
      {customer && <>
          {/* Overlay */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/30 z-40" data-id="element-1815" />
          {/* Drawer */}
          <motion.aside initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 360,
        damping: 38
      }} className="fixed top-0 right-0 bottom-0 w-full sm:w-[480px] bg-white shadow-2xl z-50 flex flex-col" data-id="element-1816">
            <ProfileContent customer={customer} onClose={onClose} branches={branches} updateCustomer={updateCustomer} toggleTag={toggleTag} data-id="element-1817" />
          </motion.aside>
        </>}
    </AnimatePresence>;
}
function ProfileContent({
  customer,
  onClose,
  branches,
  updateCustomer,
  toggleTag
}: {
  customer: Customer;
  onClose: () => void;
  branches: Branch[];
  updateCustomer: (id: string, patch: Partial<Customer>) => void;
  toggleTag: (id: string, tag: CustomerTag) => void;
}) {
  const m = getCustomerMetrics(customer);
  const initials = (customer.firstName[0] || '') + (customer.lastName[0] || '');
  const age = customer.birthDate ? Math.floor((Date.now() - new Date(customer.birthDate).getTime()) / (1000 * 60 * 60 * 24 * 365.25)) : null;
  const tenureDays = Math.floor((Date.now() - new Date(customer.joined).getTime()) / (1000 * 60 * 60 * 24));
  const preferredBranch = branches.find(b => b.id === customer.preferredBranchId);
  const sortedOrders = [...customer.orders].sort((a, b) => b.date.localeCompare(a.date));
  return <>
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100 flex items-start gap-3 flex-shrink-0" data-id="element-1818">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-200 to-orange-400 text-white flex items-center justify-center text-lg font-bold flex-shrink-0 shadow-sm" data-id="element-1819">
          {initials.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0" data-id="element-1820">
          <h2 className="text-lg font-bold text-gray-900 truncate" data-id="element-1821">
            {customer.firstName} {customer.lastName}
          </h2>
          <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap mt-1" data-id="element-1822">
            {customer.sex !== 'unspecified' && <span className="uppercase font-semibold" data-id="element-1823">
                {customer.sex === 'male' ? 'Male' : 'Female'}
              </span>}
            {age !== null && <>
                <span className="text-gray-300" data-id="element-1824">•</span>
                <span data-id="element-1825">{age} years old</span>
              </>}
            <span className="text-gray-300" data-id="element-1826">•</span>
            <span data-id="element-1827">
              Member for {tenureDays} day{tenureDays === 1 ? '' : 's'}
            </span>
          </div>
          <div className="flex items-center gap-1 mt-2 flex-wrap" data-id="element-1828">
            {customer.tags.map(t => <TagPill key={t} tag={t} data-id="element-1829" />)}
          </div>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors flex-shrink-0" aria-label="Close" data-id="element-1830">
          <X size={18} data-id="element-1831" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5" data-id="element-1832">
        {/* Quick stats */}
        <div className="grid grid-cols-2 gap-2" data-id="element-1833">
          <MiniStat icon={<ShoppingBag size={13} data-id="element-1835" />} label="Total orders" value={m.totalOrders.toString()} data-id="element-1834" />
          <MiniStat icon={<TrendingUp size={13} data-id="element-1837" />} label="Lifetime spend" value={`ETB ${Math.round(m.lifetimeSpend).toLocaleString()}`} data-id="element-1836" />
          <MiniStat icon={<Award size={13} data-id="element-1839" />} label="Avg order" value={`ETB ${Math.round(m.avgOrderValue).toLocaleString()}`} data-id="element-1838" />
          <MiniStat icon={<Clock size={13} data-id="element-1841" />} label="Last order" value={m.daysSinceLastOrder !== null ? m.daysSinceLastOrder === 0 ? 'Today' : `${m.daysSinceLastOrder}d ago` : 'Never'} highlight={m.daysSinceLastOrder !== null && m.daysSinceLastOrder > 60 ? 'warning' : undefined} data-id="element-1840" />
        </div>

        {/* Rating */}
        {m.rating !== null && <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 flex items-center gap-2" data-id="element-1842">
            <Star size={14} className="text-amber-500 fill-amber-400" data-id="element-1843" />
            <span className="text-sm font-bold text-amber-900 tabular-nums" data-id="element-1844">
              {m.rating.toFixed(1)}
            </span>
            <span className="text-xs text-amber-700" data-id="element-1845">avg rating given</span>
          </div>}

        {/* Contact info */}
        <Section title="Contact" data-id="element-1846">
          <InfoRow icon={<Phone size={13} data-id="element-1848" />} label="Phone" data-id="element-1847">
            <span className="tabular-nums" data-id="element-1849">{customer.phone}</span>
          </InfoRow>
          {customer.email && <InfoRow icon={<Mail size={13} data-id="element-1851" />} label="Email" data-id="element-1850">
              {customer.email}
            </InfoRow>}
          {customer.birthDate && <InfoRow icon={<Calendar size={13} data-id="element-1853" />} label="Birthday" data-id="element-1852">
              {new Date(customer.birthDate).toLocaleDateString(undefined, {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
            </InfoRow>}
          <InfoRow icon={<Calendar size={13} data-id="element-1855" />} label="Joined" data-id="element-1854">
            {new Date(customer.joined).toLocaleString()}
          </InfoRow>
          {preferredBranch && <InfoRow icon={<MapPin size={13} data-id="element-1857" />} label="Preferred branch" data-id="element-1856">
              {preferredBranch.name}
            </InfoRow>}
        </Section>

        {/* Behavior */}
        <Section title="Behavior" data-id="element-1858">
          {m.favoriteMethod && <InfoRow icon={<Heart size={13} data-id="element-1860" />} label="Favorite channel" data-id="element-1859">
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${methodColors[m.favoriteMethod]}`} data-id="element-1861">
                {methodLabels[m.favoriteMethod]}
              </span>
            </InfoRow>}
          {m.topItems.length > 0 && <div className="pt-2" data-id="element-1862">
              <div className="text-[10px] uppercase tracking-wide font-bold text-gray-500 mb-1.5" data-id="element-1863">
                Most ordered
              </div>
              <div className="space-y-1" data-id="element-1864">
                {m.topItems.map((it, i) => <div key={it.name} className="flex items-center justify-between text-sm" data-id="element-1865">
                    <div className="flex items-center gap-2 min-w-0" data-id="element-1866">
                      <span className="w-5 h-5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0" data-id="element-1867">
                        {i + 1}
                      </span>
                      <span className="text-gray-700 truncate" data-id="element-1868">{it.name}</span>
                    </div>
                    <span className="text-xs text-gray-500 tabular-nums flex-shrink-0" data-id="element-1869">
                      ×{it.count}
                    </span>
                  </div>)}
              </div>
            </div>}
        </Section>

        {/* Tags */}
        <Section title="Tags" data-id="element-1870">
          <div className="flex items-center gap-1.5 flex-wrap" data-id="element-1871">
            {allTags.map(tag => {
            const isActive = customer.tags.includes(tag);
            const meta = tagMeta[tag];
            return <button key={tag} onClick={() => toggleTag(customer.id, tag)} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold uppercase tracking-wide border transition-colors ${isActive ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'}`} data-id="element-1872">
                  {meta.label}
                </button>;
          })}
          </div>
        </Section>

        {/* Marketing */}
        <Section title="Marketing" data-id="element-1873">
          <div className="flex items-center justify-between gap-3" data-id="element-1874">
            <div data-id="element-1875">
              <div className="text-sm font-semibold text-gray-900" data-id="element-1876">
                Promotional messages
              </div>
              <div className="text-[11px] text-gray-500" data-id="element-1877">
                Receive offers, birthday rewards, and updates
              </div>
            </div>
            <Toggle checked={customer.marketingOptIn} onChange={v => updateCustomer(customer.id, {
            marketingOptIn: v
          })} data-id="element-1878" />
          </div>
        </Section>

        {/* Notes */}
        <Section title="Internal notes" data-id="element-1879">
          <div className="relative" data-id="element-1880">
            <MessageSquare size={13} className="absolute left-2.5 top-2.5 text-gray-400" data-id="element-1881" />
            <textarea value={customer.notes} onChange={e => updateCustomer(customer.id, {
            notes: e.target.value
          })} placeholder="Allergies, preferences, special requests..." rows={3} className="w-full pl-8 pr-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition resize-none" data-id="element-1882" />
          </div>
          <p className="text-[10px] text-gray-400 mt-1" data-id="element-1883">
            Visible to staff only — never shown to the customer.
          </p>
        </Section>

        {/* Order history */}
        <Section title="Order history" right={<span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide" data-id="element-1885">
              {sortedOrders.length} total
            </span>} data-id="element-1884">
          {sortedOrders.length === 0 ? <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-center text-xs text-gray-400" data-id="element-1886">
              No orders yet.
            </div> : <div className="space-y-2" data-id="element-1887">
              {sortedOrders.map(o => <OrderHistoryRow key={o.id} order={o} branches={branches} data-id="element-1888" />)}
            </div>}
        </Section>
      </div>
    </>;
}
function MiniStat({
  icon,
  label,
  value,
  highlight
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  highlight?: 'warning';
}) {
  return <div className={`rounded-lg border p-2.5 ${highlight === 'warning' ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white'}`} data-id="element-1889">
      <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-0.5" data-id="element-1890">
        <span className={`${highlight === 'warning' ? 'text-amber-600' : 'text-gray-400'}`} data-id="element-1891">
          {icon}
        </span>
        {label}
      </div>
      <div className={`text-base font-bold tabular-nums ${highlight === 'warning' ? 'text-amber-900' : 'text-gray-900'}`} data-id="element-1892">
        {value}
      </div>
    </div>;
}
function Section({
  title,
  children,
  right
}: {
  title: string;
  children: React.ReactNode;
  right?: React.ReactNode;
}) {
  return <section data-id="element-1893">
      <div className="flex items-center justify-between mb-2" data-id="element-1894">
        <h3 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-1895">
          {title}
        </h3>
        {right}
      </div>
      <div className="space-y-1.5" data-id="element-1896">{children}</div>
    </section>;
}
function InfoRow({
  icon,
  label,
  children
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return <div className="grid grid-cols-[20px_90px_1fr] items-center gap-2 text-sm py-1" data-id="element-1897">
      <span className="text-gray-400" data-id="element-1898">{icon}</span>
      <span className="text-[11px] text-gray-500 uppercase tracking-wide font-semibold" data-id="element-1899">
        {label}
      </span>
      <span className="text-gray-900 truncate" data-id="element-1900">{children}</span>
    </div>;
}
function OrderHistoryRow({
  order,
  branches
}: {
  order: ReturnType<typeof getCustomerMetrics> extends infer T ? any : never; // sidestep import cycle; use Customer['orders'][number]
  branches: Branch[];
}) {
  const Icon = methodIcons[order.method];
  const branch = branches.find(b => b.id === order.branchId);
  const d = new Date(order.date);
  const daysAgo = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  const dateLabel = daysAgo === 0 ? 'Today' : daysAgo === 1 ? 'Yesterday' : `${daysAgo}d ago`;
  const itemSummary = order.items.slice(0, 2).map((i: any) => `${i.qty}× ${i.name}`).join(', ');
  const extraItems = order.items.length - 2;
  return <div className="rounded-lg border border-gray-200 bg-white p-2.5" data-id="element-1901">
      <div className="flex items-start gap-2.5" data-id="element-1902">
        <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${methodColors[order.method]}`} data-id="element-1903">
          <Icon size={13} data-id="element-1904" />
        </div>
        <div className="flex-1 min-w-0" data-id="element-1905">
          <div className="flex items-center justify-between gap-2 mb-0.5" data-id="element-1906">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide" data-id="element-1907">
              <span className="text-gray-700" data-id="element-1908">
                {methodLabels[order.method]}
              </span>
              {order.status === 'cancelled' && <span className="bg-red-100 text-red-700 px-1 rounded" data-id="element-1909">
                  Cancelled
                </span>}
              {order.status === 'refunded' && <span className="bg-gray-100 text-gray-700 px-1 rounded" data-id="element-1910">
                  Refunded
                </span>}
            </div>
            <div className="font-bold text-gray-900 text-sm tabular-nums flex-shrink-0" data-id="element-1911">
              ETB {Math.round(order.total).toLocaleString()}
            </div>
          </div>
          <div className="text-xs text-gray-700 truncate" data-id="element-1912">
            {itemSummary}
            {extraItems > 0 && <span className="text-gray-400" data-id="element-1913"> +{extraItems} more</span>}
          </div>
          <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500" data-id="element-1914">
            <span data-id="element-1915">{dateLabel}</span>
            {branch && <>
                <span className="text-gray-300" data-id="element-1916">•</span>
                <span className="truncate" data-id="element-1917">{branch.name}</span>
              </>}
            {order.rating && <>
                <span className="text-gray-300" data-id="element-1918">•</span>
                <span className="inline-flex items-center gap-0.5 text-amber-600 font-bold" data-id="element-1919">
                  <Star size={9} className="fill-amber-400" data-id="element-1920" />
                  {order.rating}
                </span>
              </>}
          </div>
        </div>
      </div>
    </div>;
}